import {useMemo} from 'react'

import {usePaginatedMemexItemsQueryContext} from '../memex-items/queries/paginated-memex-items-query-context'
import {
  usePaginatedMemexItemsQuery,
  type usePaginatedMemexItemsQueryReturnType,
} from '../memex-items/queries/use-paginated-memex-items-query'

export type UsePaginatedItemsQueryStatusReturnType = Pick<
  usePaginatedMemexItemsQueryReturnType,
  'hasInitialData' | 'isFetching' | 'isRefetching'
>

/**
 * Returns the loading status for paginated items queries.
 *
 * Because of how Memex manages refreshing paginated items data, by default `usePaginatedMemexItemsQuery`
 * returns `isFetching: true` and `isRefetching: false` when fetching data for new variables or refreshing
 * data for current variables.
 * This hook overrides that behavior to return `isFetching: false` and `isRefetching: true`
 * when refreshing data for current variables.
 * @link [Refreshing items docs](https://github.com/github/projects-platform/blob/main/docs/initiatives/projects-without-limits/react-query-data.md#refreshing-items-with-multiple-sequential-requests)
 *
 * @returns {UsePaginatedItemsQueryStatusReturnType} Status object containing:
 *   - hasInitialData: Whether any items query has data
 *   - isFetching: Whether queries are fetching data for new variables (excludes refresh)
 *   - isRefetching: Whether queries are refreshing data for current variables
 */
export function usePaginatedItemsQueryStatus(): UsePaginatedItemsQueryStatusReturnType {
  const {
    hasInitialData,
    isFetching: isAnyQueryFetching,
    isRefetching: isAnyQueryRefetching,
  } = usePaginatedMemexItemsQuery()
  const context = usePaginatedMemexItemsQueryContext()
  const isRefetching = isAnyQueryRefetching || !!context?.isRefreshing
  const isFetching = isAnyQueryFetching && !isRefetching
  return useMemo(() => ({hasInitialData, isFetching, isRefetching}), [hasInitialData, isFetching, isRefetching])
}
